import React, { useCallback, useEffect, useState } from "react";
import { BookOutlined, PlusOutlined } from "@ant-design/icons";
import CcButton from "../CoilConfigurator/components/CcButton";
import ConfigurationsDashboard from "./views/ConfigurationsDashboard";
import NewConfigurationWizard, { type WizardStep } from "./views/NewConfigurationWizard";
import GeneratingView from "./views/GeneratingView";
import ConfigurationResultView from "./views/ConfigurationResultView";
import EquipmentDetailView from "./views/EquipmentDetailView";
import BuildSpecificationView from "./views/BuildSpecificationView";
import BuildSpecsPanel from "./views/BuildSpecsPanel";
import OpTabNav, { type OpTab } from "./components/OpTabNav";
import { OnPipeApiError } from "../../services/onPipeConfiguratorApi";
import { configurationService } from "./services/configurationService";
import { wizardStepForField } from "./utils/validationFieldMap";
import {
  emptyPipeSpecification,
  emptyProcessSpecification,
  emptyProjectInformation,
  type BuildSpecification,
  type CadCheck,
  type ConfigurationResult,
  type EquipmentKind,
  type EngineeringOverride,
  type PipeSpecification,
  type ProcessSpecification,
  type ProjectInformation,
  type ProjectSpecification,
} from "./models/types";
import "../CoilConfigurator/coil-configurator.css";
import "../CoilConfigurator/coil-configurator-legacy.css";
import "../CoilConfigurator/components/cc-button.css";
import "../CoilConfigurator/components/cc-dialog.css";
import "../CoilConfigurator/components/input-with-unit-select.css";
import "./on-pipe-configurator.css";

type Screen = "list" | "wizard" | "generating" | "result" | "equipment-detail";

type BuildSpecsScreen = "list" | "detail";

const WIZARD_ORDER: WizardStep[] = ["project", "pipe", "process", "review"];

const emptySpec = (): ProjectSpecification => ({
  project: emptyProjectInformation(),
  pipe: emptyPipeSpecification(),
  process: emptyProcessSpecification(),
});

const GENERATE_STEPS = [
  "Analysing pipe specification",
  "Calculating Blast Frame",
  "Calculating Induction Coil",
  "Generating configuration",
];

const OnPipeConfiguratorPage = () => {
  const [activeTab, setActiveTab] = useState<OpTab>("configurations");
  const [screen, setScreen] = useState<Screen>("list");
  const [buildSpecsScreen, setBuildSpecsScreen] = useState<BuildSpecsScreen>("list");
  const [rows, setRows] = useState<ConfigurationResult[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [spec, setSpec] = useState<ProjectSpecification>(emptySpec);
  const [step, setStep] = useState<WizardStep>("project");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ConfigurationResult | null>(null);
  const [equipmentKind, setEquipmentKind] = useState<EquipmentKind>("blast-frame");
  const [buildSpec, setBuildSpec] = useState<BuildSpecification | null>(null);
  const [generateProgress, setGenerateProgress] = useState(0);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await configurationService.listConfigurations();
      setRows(data);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const startCreate = () => {
    setActiveTab("configurations");
    setSpec(emptySpec());
    setStep("project");
    setErrors({});
    setGenerateError(null);
    setScreen("wizard");
  };

  const validateStep = (current: WizardStep) => {
    const nextErrors: Record<string, string> = {};
    if (current === "project") {
      if (!spec.project.projectNumber.trim()) nextErrors.projectNumber = "Project number is required.";
      if (!spec.project.projectName.trim()) nextErrors.projectName = "Project name is required.";
      if (!spec.project.customer.trim()) nextErrors.customer = "Customer is required.";
      if (!spec.project.engineer.trim()) nextErrors.engineer = "Engineer is required.";
    }
    if (current === "pipe") {
      if (!spec.pipe.outsideDiameter.trim() || Number(spec.pipe.outsideDiameter) <= 0) {
        nextErrors.outsideDiameter = "Enter a valid pipe outside diameter.";
      }
      if (!spec.pipe.wallThickness.trim() || Number(spec.pipe.wallThickness) <= 0) {
        nextErrors.wallThickness = "Enter a valid wall thickness.";
      }
      if (!spec.pipe.pipeGrade.trim()) nextErrors.pipeGrade = "Pipe grade is required.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const continueWizard = () => {
    if (!validateStep(step)) return;
    const index = WIZARD_ORDER.indexOf(step);
    setStep(WIZARD_ORDER[Math.min(index + 1, WIZARD_ORDER.length - 1)]);
  };

  const backWizard = () => {
    const index = WIZARD_ORDER.indexOf(step);
    setStep(WIZARD_ORDER[Math.max(index - 1, 0)]);
  };

  const generate = async () => {
    setGenerateError(null);
    setGenerateProgress(0);
    setScreen("generating");
    const timers = [0, 1, 2, 3].map((i) =>
      window.setTimeout(() => setGenerateProgress(i + 1), 280 * (i + 1)),
    );
    try {
      const created = await configurationService.createConfiguration(spec);
      timers.forEach((id) => window.clearTimeout(id));
      setGenerateProgress(4);
      setResult(created);
      setActiveTab("configurations");
      setScreen("result");
      await loadList();
    } catch (error) {
      timers.forEach((id) => window.clearTimeout(id));
      if (error instanceof OnPipeApiError && error.validationErrors?.length) {
        const fieldErrors = Object.fromEntries(
          error.validationErrors.map((item) => [item.field, item.message]),
        );
        setErrors(fieldErrors);
        setGenerateError(error.message);
        setStep(wizardStepForField(error.validationErrors[0].field));
      } else {
        setGenerateError(
          error instanceof Error
            ? error.message
            : "We couldn't generate the configuration. Please try again or contact engineering support.",
        );
        setStep("review");
      }
      setScreen("wizard");
    }
  };

  const openResult = async (id: string) => {
    const found = await configurationService.getConfiguration(id);
    setResult(found);
    setActiveTab("configurations");
    setScreen("result");
  };

  const openBuildSpec = async (kind: EquipmentKind, configurationId?: string) => {
    const id = configurationId ?? result?.configurationId;
    if (!id) return;
    const specDoc = await configurationService.getBuildSpecification(id, kind);
    setEquipmentKind(kind);
    setBuildSpec(specDoc);
    setActiveTab("build-specs");
    setBuildSpecsScreen("detail");
  };

  const toggleCad = async (checks: CadCheck[]) => {
    if (!result) return;
    const updated = await configurationService.updateCadChecks(
      result.configurationId,
      equipmentKind,
      checks,
    );
    setResult({ ...updated });
  };

  const applyOverride = async (override: EngineeringOverride) => {
    if (!result) return;
    const updated = await configurationService.applyOverride(
      result.configurationId,
      equipmentKind,
      override,
    );
    setResult({ ...updated });
  };

  return (
    <div className="app-shell coil-configurator-shell on-pipe-configurator-shell">
      <div className="coil-configurator-main">
        <header className="page-header">
          <div className="page-header__identity">
            <h1 className="page-header__title">On-Pipe Equipment Configurator</h1>
            <p className="page-header__breadcrumb">Engineering · Blast Frame + Induction Coil</p>
          </div>
          <div className="page-header__actions">
            <CcButton variant="secondary" className="glossary-button" icon={<BookOutlined />} disabled>
              Glossary
            </CcButton>
            <CcButton variant="primary" icon={<PlusOutlined />} onClick={startCreate}>
              New Configuration
            </CcButton>
          </div>
        </header>

        <div className="content-container">
          <OpTabNav
            activeTab={activeTab}
            onChange={(tab) => {
              setActiveTab(tab);
              if (tab === "build-specs") {
                setBuildSpecsScreen("list");
              }
            }}
          />

          {generateError && activeTab === "configurations" ? (
            <p className="op-field-error" role="alert">
              {generateError}
            </p>
          ) : null}

          {activeTab === "configurations" && screen === "list" && (
            <ConfigurationsDashboard
              rows={rows}
              loading={listLoading}
              onCreate={startCreate}
              onOpen={openResult}
            />
          )}

          {activeTab === "configurations" && screen === "wizard" && (
            <NewConfigurationWizard
              step={step}
              spec={spec}
              errors={errors}
              onChangeProject={(patch: Partial<ProjectInformation>) =>
                setSpec((prev) => ({ ...prev, project: { ...prev.project, ...patch } }))
              }
              onChangePipe={(patch: Partial<PipeSpecification>) =>
                setSpec((prev) => ({ ...prev, pipe: { ...prev.pipe, ...patch } }))
              }
              onChangeProcess={(patch: Partial<ProcessSpecification>) =>
                setSpec((prev) => ({ ...prev, process: { ...prev.process, ...patch } }))
              }
              onBack={backWizard}
              onContinue={continueWizard}
              onCancel={() => setScreen("list")}
              onGenerate={generate}
            />
          )}

          {activeTab === "configurations" && screen === "generating" && (
            <GeneratingView
              steps={GENERATE_STEPS.map((label, index) => ({
                label,
                state: index < generateProgress ? "done" : index === generateProgress ? "active" : "pending",
              }))}
            />
          )}

          {activeTab === "configurations" && screen === "result" && result && (
            <ConfigurationResultView
              result={result}
              onBackToList={() => setScreen("list")}
              onViewEquipment={(kind) => {
                setEquipmentKind(kind);
                setScreen("equipment-detail");
              }}
              onOpenBuildSpec={(kind) => openBuildSpec(kind)}
            />
          )}

          {activeTab === "configurations" && screen === "equipment-detail" && result && (
            <EquipmentDetailView
              result={result}
              equipmentKind={equipmentKind}
              onBack={() => setScreen("result")}
              onToggleCad={toggleCad}
              onApplyOverride={applyOverride}
            />
          )}

          {activeTab === "build-specs" && buildSpecsScreen === "list" && (
            <BuildSpecsPanel
              rows={rows}
              loading={listLoading}
              onOpenBuildSpec={(configurationId, equipment) =>
                openBuildSpec(equipment, configurationId)
              }
            />
          )}

          {activeTab === "build-specs" && buildSpecsScreen === "detail" && buildSpec && (
            <BuildSpecificationView
              spec={buildSpec}
              onBack={() => setBuildSpecsScreen("list")}
              backLabel="← Back to build specifications"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OnPipeConfiguratorPage;
